<script setup>
import { ref, watch, computed, nextTick, onBeforeMount, onMounted } from "vue";
import Sorting from "./Sorting.vue";
import ProductRow from "./ProductRow.vue";
import ProductMobileRow from "./ProductMobileRow.vue";
import ProductInfoModal from "./ProductInfoModal.vue";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { AdjustmentsIcon, ChevronDownIcon } from "@heroicons/vue/solid";
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverPortal,
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
} from "radix-vue";
import Distribution from "@/modules/Distribution";
import useDistroNav from "@/modules/DistroNav";
import { useStore } from "vuex";
import BrandsListLoader from "../../../../components/loaders/BrandsListLoader.vue";

const props = defineProps({
  inStockOnly: {
    type: Boolean,
    default: false,
  },
  neverOrdered: {
    type: Boolean,
    default: false,
  },
});

const isInfoModalVisible = ref(false);
const currentProdSlug = ref("");
const isBrandsAndCategoriesLoading = ref(false);

const openInfoModal = (slug) => {
  currentProdSlug.value = slug;
  isInfoModalVisible.value = true;
};

const { getBrandsWithCategories, brands, getProducts, products } = Distribution();

const fetchedCombos = ref([]);
const activeFilters = computed(() => ({
  in_stock: props.inStockOnly,
  never_ordered: props.neverOrdered,
}));

watch(activeFilters, () => {
  fetchedCombos.value.forEach(({ brandId, categoryId }) => {
    getProducts(brandId, categoryId, activeFilters.value, true);
  });
});

const store = useStore();
onBeforeMount(() => {
  store.dispatch("getCart", { ax: null });
});

const getImage = (brand) => {
  return process.env.BASE_URL_IMG + "/" + brand.image_url;
};

const getBrandsAndCategories = async () => {
  if (isBrandsAndCategoriesLoading.value) return;
  try {
    isBrandsAndCategoriesLoading.value = true;
    await getBrandsWithCategories();

    const productsPromises = [];
    if (brands.data.length) {
      const firstBrand = brands.data[0];
      const firstCategory = firstBrand.categories[0];
      if (firstCategory.id) {
        fetchedCombos.value.push({ brandId: firstBrand.id, categoryId: firstCategory.id });
        productsPromises.push(getProducts(firstBrand.id, firstCategory.id, activeFilters.value));
      }
      await Promise.allSettled(productsPromises)
        .finally(() => {
          isBrandsAndCategoriesLoading.value = false;
        });
    } else {
      isBrandsAndCategoriesLoading.value = false;
    }
  } catch (e) {
    console.log(e);
  }
};

onMounted(() => {
  getBrandsAndCategories();
});

const onRemoveFilter = (brand, filter) => {
  brand.categoryFilters = brand.categoryFilters.filter((r) => r.id != filter.id);
};

// ── Distro search navigation ──────────────────────────────────────────────────
const { pending, clear } = useDistroNav();

const scrollTo = (el) => {
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 80;
  window.scrollTo({ top, behavior: "smooth" });
};

const expandBtn = (btn) => {
  if (btn && btn.getAttribute("aria-expanded") !== "true") btn.click();
};

watch(
  () => pending.target,
  async (target) => {
    if (!target) return;
    clear();
    await nextTick();

    // Resolve brandSlug from brandName if needed (e.g. navigating from the side cart)
    const brandSlug = target.brandSlug ||
      brands.data.find((b) => b.name === target.brandName)?.slug;
    if (!brandSlug) return;

    // 1. Expand the brand accordion
    const brandBtn = document.querySelector(`.disc-btn[data-brand-slug="${brandSlug}"]`);
    expandBtn(brandBtn);
    const brandWrapper = brandBtn?.closest(".brand-wrapper");
    scrollTo(brandWrapper || brandBtn);

    if (target.type === "brand") return;

    // Wait for brand panel to open
    await new Promise((r) => setTimeout(r, 200));
    await nextTick();

    // 2. Find the matching brand + category from data
    const brand = brands.data.find((b) => b.slug === brandSlug);
    if (!brand) return;
    const category = brand.categories.find(
      (c) => c.name.toLowerCase() === target.categoryName.toLowerCase()
    );
    if (!category) return;

    // 3. Expand the category accordion
    const catBtn = document.querySelector(
      `.variant-header[data-brand-slug="${brandSlug}"][data-category-id="${category.id}"]`
    );
    expandBtn(catBtn);
    scrollTo(catBtn);

    if (target.type === "category") return;

    // 4. Wait for products to finish loading, then scroll to the product row
    await new Promise((r) => {
      const check = () => {
        if (!products.loading[`${brand.id}-${category.id}`]) { r(); return; }
        setTimeout(check, 80);
      };
      check();
    });
    await nextTick();
    const anchor = document.querySelector(`[data-product-slug="${target.productSlug}"]`);
    if (anchor) {
      scrollTo(anchor);
      // Collect all order-row <tr>s until the next divider
      const rows = [];
      let el = anchor.nextElementSibling;
      while (el && !el.classList.contains("product-divider-row")) {
        if (el.classList.contains("order-row")) rows.push(el);
        el = el.nextElementSibling;
      }
      rows.forEach((r) => r.classList.add("distro-highlight"));
      setTimeout(() => rows.forEach((r) => r.classList.remove("distro-highlight")), 2500);
    }
  }
);
const isVariantInStock = (variant) => {
  return Number(variant?.available_stock) > 0 && Number(variant?.MaxOrderQty) > 0;
};

const matchesVariantFilters = (variant) => {
  if (props.inStockOnly && !isVariantInStock(variant)) {
    return false;
  }

  if (props.neverOrdered && variant?.last_ordered) {
    return false;
  }

  return true;
};

const getVisibleProducts = (brandId, categoryId) => {
  const categoryProducts = products.data[brandId]?.[categoryId] || [];

  return categoryProducts
    .map((product) => ({
      ...product,
      variants: (product.variants || []).filter(matchesVariantFilters),
    }))
    .filter((product) => product.variants.length);
};

const openMediaUrl = (url) => window.open(url);
</script>

<template>
  <div class="brand-list-item">
    <template v-if="isBrandsAndCategoriesLoading">
      <BrandsListLoader v-for="x in 3" :key="x" />
    </template>
    <template v-else>
      <div class="brand-wrapper flex flex-col border border-gray-200 rounded-md overflow-visible"
        v-for="(b, bIdx) in brands.data" :key="b.id">
        <Disclosure v-slot="{ open }" :defaultOpen="true">
          <DisclosureButton class="disc-btn brand-item-inner" :data-brand-slug="b.slug">
            <div class="brand-item-info">
              <div class="item-logo">
                <img :src="getImage(b)" :alt="b.name" />
              </div>
              <div class="item-data">
                <div class="title-row">
                  <p>{{ b.name }}</p>
                </div>
                <p class="stats-meta">
                  {{ b.categories.length }} {{ b.categories.length > 1 ? "Categories" : "Category" }}
                  <span class="dot">•</span>
                  {{ b.product_count }} {{ b.product_count > 1 ? "Products" : "Product" }}
                </p>
              </div>
            </div>
            <div class="other-actions">
              <!-- <input type="text" name="search" id="search" placeholder="Search products.." class="brand-search" /> -->
              <!-- <Sorting /> -->
              <PopoverRoot v-if="open">
                <PopoverTrigger as-child>
                  <button type="button" @click.stop
                    :class="['border border-gray-200 w-full relative inline-flex items-center justify-between py-1.5 pl-4 pr-4 text-left text-sm font-normal bg-white cursor-pointers text-brand-secondary focus:outline-none rounded', b.categoryFilters?.length ? 'sm:w-48' : 'sm:w-36']">
                    <AdjustmentsIcon class="flex-shrink-0 h-4 w-4 mr-2 text-brand-body" aria-hidden="true" />
                    <span>Category</span>
                    <span v-if="b.categoryFilters?.length" class="filter-count-badge">{{ b.categoryFilters.length
                      }}</span>
                    <ChevronDownIcon class="flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-brand-body" aria-hidden="true" />
                  </button>
                </PopoverTrigger>

                <PopoverPortal>
                  <PopoverContent @click.stop align="end" :side-offset="8"
                    class="w-80 origin-top-right bg-white rounded shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div class="space-y-3 p-3 border-b border-brand-ash border-opacity-60">
                      <form class="relative w-full" autocomplete="off">
                        <input id="searchcat" v-model="b.categoriesSearchQuery" name="search-cat"
                          class="appearance-none block text-xs font-light w-full pl-5 pr-3 py-1.5 border border-none rounded-full leading-5 bg-brand-ash bg-opacity-20 placeholder-text focus:outline-none sm:text-sm focus:ring-brand-smoke"
                          placeholder="Search Categories" type="search" autocomplete="chrome-off" />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <SearchIcon class="h-4 w-4 text-brand-ash" aria-hidden="true" />
                        </div>
                      </form>
                      <div class="-m-1 flex flex-wrap items-center" v-if="b.categoryFilters.length">
                        <span v-for="activeFilter in b.categoryFilters" :key="activeFilter.id"
                          class="m-1 inline-flex rounded-full border border-gray-200 items-center py-1.5 pl-3 pr-2 text-xs font-medium bg-white text-brand-body">
                          <span>{{ activeFilter.name }}</span>
                          <button type="button"
                            class="flex-shrink-0 ml-1 h-4 w-4 p-1 rounded-full inline-flex text-gray-400 hover:bg-gray-200 hover:text-gray-500"
                            @click="onRemoveFilter(b, activeFilter)">
                            <svg class="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                              <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
                            </svg>
                          </button>
                        </span>
                      </div>
                    </div>

                    <ScrollAreaRoot type="always" class="w-full h-60 overflow-hidden">
                      <ScrollAreaViewport class="w-full h-full">
                        <div class="space-y-4 p-4 pr-5">
                          <div v-for="option in b.categoriesSearchQuery
                            ? b.categories.filter((r) => r.name.toLowerCase().includes(b.categoriesSearchQuery.toLowerCase()))
                            : b.categories" :key="option.id" class="flex items-center justify-between">
                            <div class="flex items-center">
                              <input :id="`cat-${option.id}`" name="catfilter" v-model="b.categoryFilters"
                                :value="option" type="checkbox"
                                class="h-4 w-4 border-2 border-brand-secondary opacity-60 rounded text-brand-secondary focus:ring-transparent" />
                              <label :for="`cat-${option.id}`"
                                class="ml-2 leading-none pr-6 text-sm font-medium text-brand-secondary text-opacity-70 whitespace-nowrap">
                                {{ option.name }}
                              </label>
                            </div>
                            <span class="text-xs text-brand-body text-opacity-50">{{ option.stock }}</span>
                          </div>
                        </div>
                      </ScrollAreaViewport>

                      <ScrollAreaScrollbar force-mount orientation="vertical"
                        class="w-2.5 flex select-none touch-none p-0.5 bg-gray-100 border-l border-gray-200">
                        <ScrollAreaThumb class="relative flex-1 min-h-8 rounded-full bg-gray-400" />
                      </ScrollAreaScrollbar>
                    </ScrollAreaRoot>
                  </PopoverContent>
                </PopoverPortal>
              </PopoverRoot>
              <button class="download-btn" v-if="open && b.media_file_url" @click="openMediaUrl(b.media_file_url)">
                <Icon name="document-file" class="w-3.5 h-3.5 sm:w-4 sm:h-4 -mt-0.5" />
                <span>Brand Media / Sell Sheets</span>
              </button>
              <template v-if="isBrandsAndCategoriesLoading">
                <Spinner className="w-6 h-6" :loading="true" />
              </template>
              <template v-else>
                <Icon class="w-6 h-6 transform transition-transform text-theme-body" name="down-arrow"
                  :class="open ? 'rotate-180' : ''" />
              </template>
            </div>
          </DisclosureButton>
          <transition enter-active-class="transition ease-out duration-100" enter-from-class="opacity-0"
            enter-to-class="opacity-100" leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100" leave-to-class="opacity-0">
            <DisclosurePanel as="div" class="brand-list">
              <Disclosure v-for="(c, idx) in (b.categoryFilters.length ? b.categoryFilters : b.categories)" :key="c.id"
                :defaultOpen="bIdx == 0 && idx == 0" v-slot="{ open }">
                <div class="variant-list">
                  <DisclosureButton class="variant-header" @click="() => { if (!fetchedCombos.find(x => x.brandId === b.id && x.categoryId === c.id)) fetchedCombos.push({ brandId: b.id, categoryId: c.id }); getProducts(b.id, c.id, activeFilters); }" :data-brand-slug="b.slug"
                    :data-category-id="c.id">
                    <p class="flex items-center space-x-2">
                      <Icon name="all-categories" />
                      <span class="pt-0.5">{{ c.name }}</span>
                    </p>
                    <template v-if="products.loading[`${b.id}-${c.id}`]">
                      <Spinner className="h-6 w-6" :loading="true" />
                    </template>
                    <template v-else>
                      <Icon class="w-6 h-6 transform transition-transform text-theme-body" name="down-arrow"
                        :class="open ? 'rotate-180' : ''" />
                    </template>
                  </DisclosureButton>
                  <Transition enter-active-class="transition ease-out duration-100" enter-from-class="opacity-0"
                    enter-to-class="opacity-100" leave-active-class="transition ease-in duration-75"
                    leave-from-class="opacity-100" leave-to-class="opacity-0" v-if="products.data[b.id]">
                    <DisclosurePanel as="div">
                      <table class="product-table hidden w-full lg:table">
                        <thead class="product-table-head">
                          <tr>
                            <th
                              class="px-5 py-2.5 text-xs font-normal text-brand-body text-left border-b border-t border-gray-100">
                              <span>Product</span>
                            </th>
                            <th
                              class="px-5 py-2.5 text-xs font-normal text-brand-body text-left pl-20 border-b border-t border-gray-100">
                              <span>Variant</span>
                            </th>
                            <th
                              class="px-5 py-2.5 text-xs font-normal text-brand-body text-left border-b border-t border-gray-100">
                              <span>Price</span>
                            </th>
                            <th
                              class="px-5 py-2.5 text-xs font-normal text-brand-body text-left border-b border-t border-gray-100">
                              <span>Wholesale Price</span>
                            </th>
                            <th
                              class="px-5 py-2.5 text-xs font-normal text-brand-body text-left border-b border-t border-gray-100">
                              <span>Last Ordered</span>
                            </th>
                            <th
                              class="px-5 py-2.5 text-xs font-normal text-brand-body text-left border-b border-t border-gray-100">
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-if="products.loading[`${b.id}-${c.id}`]">
                            <tr>
                              <td colspan="10">
                                <BrandsListLoader v-for="y in 3" :key="y" />
                              </td>
                            </tr>
                          </template>
                          <template v-else-if="getVisibleProducts(b.id, c.id).length">
                            <template v-for="p in getVisibleProducts(b.id, c.id)" :key="p.id">
                              <tr :data-product-slug="p.slug" style="height:0;line-height:0;"></tr>
                              <ProductRow v-for="(v, i) in p.variants" :key="v.id" :index="i" :product="p" :variant="v"
                                @onProductClick="openInfoModal" />
                              <tr class="product-divider-row">
                                <td colspan="10" class="border-t border-gray-100" />
                              </tr>
                            </template>
                          </template>
                          <tr v-else>
                            <td colspan="10" class="px-5 py-6 text-sm text-brand-body text-opacity-70">
                              No products match the selected filters.
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div class="mobile-product-list lg:hidden">
                        <template v-if="products.loading[`${b.id}-${c.id}`]">
                          <BrandsListLoader v-for="y in 3" :key="y" />
                        </template>
                        <template v-else-if="getVisibleProducts(b.id, c.id).length">
                          <template v-for="p in getVisibleProducts(b.id, c.id)" :key="`${p.id}-mobile`">
                            <ProductMobileRow v-for="v in p.variants" :key="`${v.id}-mobile`" :product="p" :variant="v"
                              @onProductClick="openInfoModal" />
                          </template>
                        </template>
                        <div v-else class="px-4 py-6 text-sm text-brand-body text-opacity-70">
                          No products match the selected filters.
                        </div>
                      </div>
                    </DisclosurePanel>
                  </Transition>
                </div>
              </Disclosure>
            </DisclosurePanel>
          </transition>
        </Disclosure>
      </div>
    </template>

    <ModalFull :is-visible="isInfoModalVisible" :reset-container="true" container-class="w-full max-w-5xl z-maximum"
      @close="isInfoModalVisible = false">
      <div class="product-info-container">
        <ProductInfoModal :productSlug="currentProdSlug" @close="isInfoModalVisible = false" />
      </div>
    </ModalFull>
  </div>
</template>

<style lang="scss">
@keyframes distro-highlight-pulse {
  0%   { background: transparent; }
  15%  { background: rgba(74, 85, 184, 0.10); }
  35%  { background: rgba(74, 85, 184, 0.06); }
  55%  { background: rgba(74, 85, 184, 0.10); }
  75%  { background: rgba(74, 85, 184, 0.04); }
  100% { background: transparent; }
}

.distro-highlight {
  animation: distro-highlight-pulse 2.5s ease forwards;
}
</style>

<style lang="scss" scoped>
.product-info-container {
  @apply w-full overflow-y-auto bg-theme-bg rounded-lg p-4 relative text-left;
  min-height: 200px;
  max-height: 80vh;
  @include CssScrollbar();
}

.brand-search {
  @apply w-full max-w-lg text-sm py-1.5 focus:ring-transparent focus:outline-none border border-gray-300 bg-white bg-opacity-70 text-brand-secondary placeholder-gray-400 rounded hover:border-indigo-500 transition-colors;
}

.brand-actions {
  @apply flex items-center justify-between mb-8;
}

.brand-list-item {
  @apply w-full flex flex-col gap-4;

  .product-table {
    @apply w-full;
  }

  .mobile-product-list {
    @apply p-3 space-y-3;
  }

  .brand-item-inner {
    @apply w-full flex flex-col items-start gap-3 px-4 sm:px-5 py-4 rounded-md text-brand-body bg-white transition-colors duration-500 lg:flex-row lg:items-center lg:justify-between;
    border-bottom: 1px solid #f3f4f6;
  }

  .other-actions {
    @apply flex w-full flex-wrap items-center gap-2 lg:w-auto lg:flex-nowrap lg:gap-4;
  }

  .brand-item-info {
    @apply flex space-x-4 items-start sm:items-center;

    .item-logo {
      @apply h-10 w-10 sm:h-11 sm:w-11 flex items-center justify-center rounded-md flex-shrink-0 border border-brand-ash overflow-hidden;
      background: linear-gradient(180deg, #ffffff 0%, #f8f9fc 100%);

      img {
        @apply object-contain;
        max-width: calc(100% - 8px);
        max-height: calc(100% - 8px);
      }
    }

    .item-data {
      @apply flex flex-col items-start min-w-0;

      .title-row {
        @apply flex flex-col lg:flex-row lg:items-center lg:space-x-3 w-full;

        p {
          @apply text-lg sm:text-lg font-semibold text-brand-primary leading-none;
          letter-spacing: -0.01em;
        }
      }

      .stats-meta {
        @apply text-xs sm:text-sm text-brand-body font-normal;
        opacity: 0.75;

        .dot {
          @apply mx-1.5;
        }
      }

    }
  }

  .filter-count-badge {
    @apply inline-flex items-center justify-center rounded-full text-white text-xxs font-medium ml-2;
    min-width: 18px;
    height: 18px;
    line-height: 18px;
    background: #4a55b8;
  }

  .brand-list {
    @apply flex flex-col w-full bg-white;

    .header-item {
      @apply px-5 py-1.5 text-sm font-light text-brand-body;

      span {
        @apply text-sm font-light text-brand-body;
      }
    }

    .varinat-item-cell {
      @apply px-5 py-1.5 flex items-center flex-shrink-0;
    }

    .list-header {
      @apply flex items-stretch border-t border-b border-theme-border border-opacity-60;

      .header-item {
        @apply flex-shrink-0;
      }

      .category {
        @apply w-full;
        max-width: 180px;
      }

      .item {
        @apply w-full pl-20;
        max-width: 320px;
      }

      .price {
        @apply w-full;
        max-width: 150px;
      }

      .last-ordered {
        @apply w-full;
        max-width: 300px;
      }
    }

    .variant-list {
      @apply flex flex-col;

      .variant-header {
        @apply flex items-center justify-between px-5 py-3 border-t;
        background: #fcfbff;
        border-color: #f0ebff;

        p {
          @apply text-base font-normal;
          color: #4a55b8;
        }
      }
    }

    .variant-item {
      @apply flex items-stretch py-2 border-b border-brand-ash border-opacity-60;

      &:last-child {
        @apply border-0;
      }

      .category {
        @apply w-full flex flex-col leading-none items-start;
        max-width: 180px;

        p {
          @apply text-sm text-theme-secondary;
        }

        span {
          @apply text-xs text-theme-light uppercase;
        }
      }

      .item {
        @apply w-full space-x-4;
        max-width: 320px;

        .item-img {
          @apply w-12 h-12 border border-theme-border border-opacity-60 rounded bg-white flex-shrink-0 cursor-pointer;

          img {
            @apply w-full h-auto;
          }
        }

        .item-name {
          @apply text-sm text-theme-secondary flex-grow cursor-pointer hover:underline;
          min-width: 150px;
        }

        .stock-info {
          @apply text-xxs text-brand-gray uppercase flex-shrink-0;
          width: 120px;
        }
      }

      .price {
        @apply w-full;
        max-width: 150px;

        p {
          @apply text-sm text-theme-secondary flex-grow;
        }
      }

      .last-ordered {
        @apply w-full flex-grow;
        max-width: 300px;

        span {
          @apply text-sm text-theme-body;
          min-width: 120px;
        }

        .order-badge {
          @apply text-xs font-normal min-w-0 flex-grow-0 inline-flex text-center items-center justify-center w-auto rounded-full px-5 py-2 leading-none;
          background: rgba(156, 228, 144, 0.31);
          color: #41b871;

          svg {
            @apply w-3.5 h-3.5 mr-1;
          }
        }
      }

      .rest-info {
        @apply flex-grow flex justify-end space-x-5;

        .qty-field {
          @apply flex justify-end;
          min-width: 150px;
        }
      }
    }
  }
}

.notify-bell {
  @apply flex items-center space-x-3 uppercase text-xs tracking-tight;

  .n-icon {
    @apply inline-flex items-center justify-center w-8 h-8 bg-brand-red text-white rounded;

    svg {
      @apply w-4 h-4;
    }
  }

  &.undo {
    .n-icon {
      @apply bg-theme-secondary;
    }
  }
}

.download-btn {
  @apply w-auto flex-shrink-0 mr-5 inline-flex items-center text-xxs text-white bg-theme-secondary hover:bg-opacity-95 space-x-0.5 sm:space-x-1 tracking-wide px-2.5 py-2 rounded leading-none border border-theme-secondary hover:text-white transition-colors;

  span {
    @apply hidden sm:inline;
  }

  @media (min-width: 1024px) {
    span {
      @apply inline;
    }
  }
}
</style>
